import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Spinner from '../Spinner';
import ChangePassword from './ChangePassword';
import { useEditProfileMutation } from '../../features/api/rest/apiProfileSlice';
import { useGetGeoNamesBySearchQuery, useGetEntitiesInfoByIdQuery, useLazyGetGeoNameByGeonameIdAndLangQuery, useLazyGetGeoCountryInfoByIdQuery } from '../../features/api/rest/apiGeo';
import { toDataSnakeCase } from '../../utils/profileUtils';
import { setAlertMessage } from '../../features/layoutSlice';
import useGetGeoNameByGeonameIdAndLang from '../../hooks/getGeoName';

function ProfileEdit({
  profile = {
    id: null,
    username: '',
    displayName: '',
    email: '',
    birthday: null,
    streetAddress: '',
    zipCode: '',
    city: null,
    country: null,
    gender: '',
    familyIncome: null,
    maritalStatus: '',
    educationalLevel: null,
  },
  setMode,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editProfileData, setEditProfileData] = React.useReducer((state, newState) => ({ ...state, ...newState }), profile);
  const [editProfileMutationTrigger, { isLoading, isError, error }] = useEditProfileMutation();
  const [city, setCity] = React.useState(useGetGeoNameByGeonameIdAndLang(profile?.city, 'city') || '');
  const [cityID, setCityID] = React.useState(profile?.city || '');
  const [country, setCountry] = React.useState(useGetGeoNameByGeonameIdAndLang(profile.country, 'country') || '');
  const [countryID, setCountryID] = React.useState(profile?.country || '');
  const [getGeoName] = useLazyGetGeoNameByGeonameIdAndLangQuery();
  const [getGeoCountryInfo] = useLazyGetGeoCountryInfoByIdQuery();

  const { geoCities } = useGetGeoNamesBySearchQuery(city, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data }) => ({
      geoCities: data?.filter((item) => {
        return item?.type === 'City' && item?.language === window.navigator.language.split('-')[0];
      }),
    }),
  });
  const { geoCountries } = useGetGeoNamesBySearchQuery(country, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data }) => ({
      geoCountries: data?.filter((item) => {
        return item?.type === 'Country' && item?.language === window.navigator.language.split('-')[0];
      }),
    }),
  });
  const { data, refetch } = useGetEntitiesInfoByIdQuery(cityID || countryID, { refetchOnMountOrArgChange: true }, { skip: !cityID && !countryID });

  const hasChanged = React.useMemo(() => {
    return Object.keys(editProfileData).some((key) => editProfileData[key] !== profile[key]);
  }, [editProfileData, profile]);

  const handleEditProfileDataChange = (event) => {
    const { name, value } = event.target;
    setEditProfileData({ [name]: value });
  };

  const handleEditProfileDataSubmit = async (event) => {
    event.preventDefault();
    try {
      await editProfileMutationTrigger(toDataSnakeCase(editProfileData)).unwrap();
      setMode('view');
      dispatch(setAlertMessage({ text: t('profile.editForm.messages.success'), type: 'success' }));
    } catch (err) {
      dispatch(setAlertMessage({ text: t('profile.editForm.messages.error'), type: 'error' }));
    }
  };

  const handleEditProfileDataCancel = () => {
    setEditProfileData(profile);
    setMode('view');
  };

  useEffect(() => {
    if (data) {
      setEditProfileData({ city: data?.city_id });
      setEditProfileData({ country: data?.country_id });
    }
  }, [data]);

  useEffect(() => {
    if (editProfileData?.country) {
      setCountryID(editProfileData?.country);
      getGeoCountryInfo(editProfileData?.country).then((res) => {
        getGeoName({ geonameId: res?.data?.geoname_id, lang: window.navigator.language.split('-')[0] }).then((res) => {
          setCountry(res?.data?.name);
        });
      });
    }
  }, [editProfileData?.country]);

  const setCitySelected = (citySelected, geonameId) => {
    if (citySelected !== undefined && geonameId !== undefined) {
      setCity(citySelected);
      setCityID(geonameId);
      refetch();
    } else {
      setCity('');
      setCityID('');
      setEditProfileData({ city: '' });
    }
  };

  const setCountrySelected = (countrySelected, geonameId) => {
    if (countrySelected !== undefined && geonameId !== undefined) {
      setCountry(countrySelected);
      setCountryID(geonameId);
      refetch();
    } else {
      setCountry('');
      setCountryID('');
      setEditProfileData({ country: '' });
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <Grid data-testid="ProfileEdit" component="form" container onSubmit={(e) => e.preventDefault()} spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          error={editProfileData?.username === ''}
          id="username"
          label={t('profile.editForm.fields.username.label')}
          name="username"
          autoComplete="username"
          value={editProfileData?.username}
          fullWidth
          onChange={handleEditProfileDataChange}
          inputProps={{ 'data-testid': 'Username' }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id="displayName"
          label={t('profile.editForm.fields.displayName.label')}
          name="displayName"
          autoComplete="displayName"
          value={editProfileData?.displayName}
          fullWidth
          onChange={handleEditProfileDataChange}
          inputProps={{ 'data-testid': 'DisplayName' }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id="email"
          label={t('profile.editForm.fields.email.label')}
          name="email"
          autoComplete="email"
          value={editProfileData?.email}
          fullWidth
          onChange={handleEditProfileDataChange}
          error={!!error?.data?.email}
          helperText={isError ? error?.data?.email : ''}
          inputProps={{ 'data-testid': 'Email' }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ChangePassword />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label={t('profile.editForm.fields.birthday.label')}
          name="birthday"
          value={dayjs(editProfileData?.birthday)}
          onChange={(newValue) => {
            setEditProfileData({ birthday: newValue ? dayjs(newValue).format('YYYY-MM-DD') : null });
          }}
          slotProps={{
            textField: {
              sx: { width: '100%' },
              inputProps: {
                'data-testid': 'Birthday',
                placeholder: t('common.dates.placeholder'),
              },
            },
          }}
          disableFuture
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="gender-select-label">{t('profile.editForm.fields.gender.label')}</InputLabel>
          <Select labelId="gender-select-label" id="gender-select" name="gender" value={editProfileData?.gender} label={t('profile.editForm.fields.gender.label')} onChange={handleEditProfileDataChange} inputProps={{ 'data-testid': 'Gender' }}>
            <MenuItem value="">{t('profile.editForm.fields.gender.choices.none')}</MenuItem>
            <MenuItem value="F">{t('profile.editForm.fields.gender.choices.F')}</MenuItem>
            <MenuItem value="M">{t('profile.editForm.fields.gender.choices.M')}</MenuItem>
            <MenuItem value="O">{t('profile.editForm.fields.gender.choices.O')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id="streetAddress-input"
          label={t('profile.editForm.fields.streetAddress.label')}
          name="streetAddress"
          autoComplete="street-address"
          value={editProfileData?.streetAddress}
          fullWidth
          onChange={handleEditProfileDataChange}
          inputProps={{ 'data-testid': 'StreetAddress' }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField id="zipCode-input" label={t('profile.editForm.fields.zipCode.label')} name="zipCode" autoComplete="postal-code" value={editProfileData?.zipCode} fullWidth onChange={handleEditProfileDataChange} inputProps={{ 'data-testid': 'ZipCode' }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          id="city-input"
          options={geoCities || []}
          value={city}
          fullWidth
          onChange={(e, val) => setCitySelected(val?.name, val?.geoname_id)}
          getOptionLabel={(option) => option?.display_name || city}
          // eslint-disable-next-line react/jsx-props-no-spreading
          renderInput={(params) => <TextField {...params} label={t('profile.editForm.fields.city.label')} name="city" onChange={(e) => setCity(e.target.value)} />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          id="country-input"
          options={geoCountries || []}
          value={country}
          fullWidth
          onChange={(e, val) => setCountrySelected(val?.name, val?.geoname_id)}
          getOptionLabel={(option) => option?.display_name || country}
          // eslint-disable-next-line react/jsx-props-no-spreading
          renderInput={(params) => <TextField {...params} label={t('profile.editForm.fields.country.label')} name="country" onChange={(e) => setCountry(e.target.value)} />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="familyIncome-select-label">{t('profile.editForm.fields.familyIncome.label')}</InputLabel>
          <Select
            labelId="familyIncome-select-label"
            id="familyIncome-select"
            name="familyIncome"
            value={editProfileData?.familyIncome}
            label={t('profile.editForm.fields.familyIncome.label')}
            onChange={handleEditProfileDataChange}
            inputProps={{ 'data-testid': 'FamilyIncome' }}
          >
            <MenuItem value="">{t('profile.editForm.fields.familyIncome.choices.none')}</MenuItem>
            <MenuItem value="0">{t('profile.editForm.fields.familyIncome.choices.0')}</MenuItem>
            <MenuItem value="1">{t('profile.editForm.fields.familyIncome.choices.1')}</MenuItem>
            <MenuItem value="2">{t('profile.editForm.fields.familyIncome.choices.2')}</MenuItem>
            <MenuItem value="3">{t('profile.editForm.fields.familyIncome.choices.3')}</MenuItem>
            <MenuItem value="4">{t('profile.editForm.fields.familyIncome.choices.4')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="maritalStatus-select-label">{t('profile.editForm.fields.maritalStatus.label')}</InputLabel>
          <Select
            labelId="maritalStatus-select-label"
            id="maritalStatus-select"
            name="maritalStatus"
            value={editProfileData?.maritalStatus}
            label={t('profile.editForm.fields.maritalStatus.label')}
            onChange={handleEditProfileDataChange}
            inputProps={{ 'data-testid': 'MaritalStatus' }}
          >
            <MenuItem value="">{t('profile.editForm.fields.maritalStatus.choices.none')}</MenuItem>
            <MenuItem value="S">{t('profile.editForm.fields.maritalStatus.choices.S')}</MenuItem>
            <MenuItem value="M">{t('profile.editForm.fields.maritalStatus.choices.M')}</MenuItem>
            <MenuItem value="D">{t('profile.editForm.fields.maritalStatus.choices.D')}</MenuItem>
            <MenuItem value="W">{t('profile.editForm.fields.maritalStatus.choices.W')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="educationalLevel-select-label">{t('profile.editForm.fields.educationalLevel.label')}</InputLabel>
          <Select
            labelId="educationalLevel-select-label"
            id="educationalLevel-select"
            name="educationalLevel"
            value={editProfileData?.educationalLevel}
            label={t('profile.editForm.fields.educationalLevel.label')}
            onChange={handleEditProfileDataChange}
            inputProps={{ 'data-testid': 'EducationalLevel' }}
          >
            <MenuItem value="">{t('profile.editForm.fields.educationalLevel.choices.none')}</MenuItem>
            <MenuItem value="0">{t('profile.editForm.fields.educationalLevel.choices.0')}</MenuItem>
            <MenuItem value="1">{t('profile.editForm.fields.educationalLevel.choices.1')}</MenuItem>
            <MenuItem value="2">{t('profile.editForm.fields.educationalLevel.choices.2')}</MenuItem>
            <MenuItem value="3">{t('profile.editForm.fields.educationalLevel.choices.3')}</MenuItem>
            <MenuItem value="4">{t('profile.editForm.fields.educationalLevel.choices.4')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sx={{ display: 'flex' }}>
        <Button type="submit" variant="contained" color="primary" onClick={handleEditProfileDataSubmit} disabled={!hasChanged}>
          {t('profile.editForm.fields.submit')}
        </Button>
        <Button type="button" variant="contained" color="secondary" onClick={handleEditProfileDataCancel} sx={{ ml: 2 }}>
          {t('profile.editForm.fields.cancel')}
        </Button>
      </Grid>
    </Grid>
  );
}

ProfileEdit.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    username: PropTypes.string,
    displayName: PropTypes.string,
    email: PropTypes.string,
    birthday: PropTypes.string,
    streetAddress: PropTypes.string,
    zipCode: PropTypes.string,
    city: PropTypes.number,
    country: PropTypes.number,
    gender: PropTypes.string,
    familyIncome: PropTypes.number,
    maritalStatus: PropTypes.string,
    educationalLevel: PropTypes.number,
  }),
  setMode: PropTypes.func.isRequired,
};

export default ProfileEdit;
